import { formatearFecha } from "src/utils/formatTime";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";

/**
 * Mapea una propuesta de tipo PLAN para exportación a Excel
 */
export const mapPropuestaPlan = (propuesta, sucursal) => {
  const baseData = {
    id: propuesta.id,
    tipo: propuesta.tipo,
    estado:
      propuesta.estado === "ACEPTADO_CLIENTE"
        ? "ACEPTADO POR EL CLIENTE"
        : propuesta.estado,
    fechaCreacion: formatearFecha(propuesta.fechaCreacion, "DD/MM/YYYY"),
    fechaEnvioCorreo: propuesta.fechaEnvioCorreo
      ? formatearFecha(propuesta.fechaEnvioCorreo, "DD/MM/YYYY")
      : "No enviada",
    sucursal,
    urlPropuesta: propuesta.urlPropuesta ?? "Sin propuesta cargada",
    motivoRechazo: propuesta.motivoRechazo ?? "N/A",
    comentarios: propuesta.comentarios || "Sin comentarios",

    // Información del vendedor y supervisor
    nombreVendedor: propuesta.nombreVendedor ?? "Sin vendedor",
    rutVendedor: propuesta.rutVendedor
      ? formatRut(propuesta.rutVendedor, RutFormat.DOTS_DASH)
      : "N/A",
    nombreSupervisor: propuesta.nombreSupervisor ?? "Sin supervisor",
    rutSupervisor: propuesta.rutSupervisor
      ? formatRut(propuesta.rutSupervisor, RutFormat.DOTS_DASH)
      : "N/A",

    // Información del solicitante
    nombreSolicitante: propuesta.nombreSolicitante ?? "N/A",
    rutSolicitante: propuesta.rutSolicitante
      ? formatRut(propuesta.rutSolicitante, RutFormat.DOTS_DASH)
      : "N/A",
    correoSolicitante: propuesta.correoSolicitante ?? "N/A",
    telefonoSolicitante: propuesta.telefonoSolicitante ?? "N/A",

    // Información del cliente (específico de PLAN)
    rutCliente: "N/A",
    nombreCliente: "N/A",
    correoCliente: "N/A",

    // Servicios/Planes
    servicios: "Sin planes",
    precios: "N/A",
    tipoMoneda: "N/A",
    descuentos: "N/A",

    // Encargados
    rutEncargados: "N/A",
    nombreEncargados: "N/A",
    correoEncargados: "N/A",
  };

  // Procesar planes si existen
  if (propuesta.planes?.length > 0) {
    // Cliente del primer plan
    if (propuesta.planes[0]?.cliente) {
      baseData.rutCliente = propuesta.planes[0].cliente.rut
        ? formatRut(propuesta.planes[0].cliente.rut, RutFormat.DOTS_DASH)
        : "Sin RUT";
      baseData.nombreCliente =
        propuesta.planes[0].cliente.nombre || "Sin nombre";
      baseData.correoCliente =
        propuesta.planes[0].cliente.correo || "Sin correo";
    } else {
      baseData.rutCliente = "Sin RUT";
      baseData.nombreCliente = "Cliente no existe";
      baseData.correoCliente = "Sin correo";
    }

    // Consolidar información de planes
    baseData.servicios = propuesta.planes
      .map((plan) => plan.nombrePlan)
      .join(" | ");

    baseData.precios = propuesta.planes
      .map((plan) => plan.valorPlan)
      .join(" | ");

    baseData.tipoMoneda = propuesta.planes
      .map((plan) => plan.tipoMonedaPlan)
      .join(" | ");

    baseData.descuentos = propuesta.planes
      .map((plan) => `${plan.descuento}%`)
      .join(" | ");

    baseData.rutEncargados = propuesta.planes
      .map((plan) =>
        plan.empleado?.rut
          ? formatRut(plan.empleado.rut, RutFormat.DOTS_DASH)
          : "Sin RUT",
      )
      .join(" | ");

    baseData.nombreEncargados = propuesta.planes
      .map((plan) =>
        plan.empleado
          ? `${plan.empleado.nombre} ${plan.empleado.apellidos}`.trim()
          : "Sin encargado",
      )
      .join(" | ");

    baseData.correoEncargados = propuesta.planes
      .map((plan) => plan.empleado?.correo || "Sin correo")
      .join(" | ");
  }

  return baseData;
};

/**
 * Mapea una propuesta de tipo TAREA para exportación a Excel
 */
export const mapPropuestaGeneral = (propuesta, sucursal) => ({
  id: propuesta.id,
  tipo: propuesta.tipo,
  estado:
    propuesta.estado === "ACEPTADO_CLIENTE"
      ? "ACEPTADO POR EL CLIENTE"
      : propuesta.estado,
  fechaCreacion: formatearFecha(propuesta.fechaCreacion, "DD/MM/YYYY"),
  fechaEnvioCorreo: propuesta.fechaEnvioCorreo
    ? formatearFecha(propuesta.fechaEnvioCorreo, "DD/MM/YYYY")
    : "No enviada",
  sucursal,
  urlPropuesta: propuesta.urlPropuesta || "Sin propuesta cargada",
  motivoRechazo: propuesta.motivoRechazo ?? "N/A",
  comentarios: propuesta.comentarios || "Sin comentarios",

  // Información del vendedor
  nombreVendedor: propuesta.vendidoTaxtic
    ? "Vendido Taxtic"
    : propuesta.nombreVendedor,
  rutVendedor: propuesta.vendidoTaxtic
    ? "-"
    : propuesta.rutVendedor
      ? formatRut(propuesta.rutVendedor, RutFormat.DOTS_DASH)
      : "N/A",
  nombreSupervisor: propuesta.nombreSupervisor ?? "N/A",
  rutSupervisor: propuesta.rutSupervisor
    ? formatRut(propuesta.rutSupervisor, RutFormat.DOTS_DASH)
    : "N/A",

  // Información del solicitante (en TAREA generalmente es el mismo cliente)
  nombreSolicitante:
    propuesta.nombreSolicitante || propuesta.nombreCliente || "N/A",
  rutSolicitante:
    propuesta.rutSolicitante || propuesta.rutCliente
      ? formatRut(
          propuesta.rutSolicitante || propuesta.rutCliente,
          RutFormat.DOTS_DASH,
        )
      : "N/A",
  correoSolicitante:
    propuesta.correoSolicitante || propuesta.correoCliente || "N/A",
  telefonoSolicitante: propuesta.telefonoSolicitante || "N/A",

  // Información del cliente
  rutCliente: propuesta.rutCliente
    ? formatRut(propuesta.rutCliente, RutFormat.DOTS_DASH)
    : "N/A",
  nombreCliente: propuesta.nombreCliente || "N/A",
  correoCliente: propuesta.correoCliente || "N/A",

  // Servicios (TAREA solo tiene un servicio)
  servicios: propuesta.nombreTarea || "N/A",
  precios: propuesta.precioFinal?.toString() || "0",
  tipoMoneda: propuesta.tipoMoneda || "N/A",
  descuentos: "0%",

  // Encargado
  rutEncargados: propuesta.rutEmpleado
    ? formatRut(propuesta.rutEmpleado, RutFormat.DOTS_DASH)
    : "N/A",
  nombreEncargados: propuesta.nombreEmpleado || "N/A",
  correoEncargados: propuesta.correoEmpleado || "N/A",
});

/**
 * Mapea array de propuestas mixtas (PLAN y TAREA) para exportación
 */
export const mapPropuestasParaExportacion = (propuestas, sucursal) => {
  if (!propuestas?.length) return [];

  return propuestas.map((propuesta) =>
    propuesta.tipo === "PLAN"
      ? mapPropuestaPlan(propuesta, sucursal)
      : mapPropuestaGeneral(propuesta, sucursal),
  );
};

/**
 * Genera nombre de archivo para exportación de propuestas
 */
export const generarNombreArchivoExportacion = (
  estado,
  fechaInicio,
  fechaFin,
  sucursal,
) => {
  const fechaActual = new Date().toISOString().split("T")[0];
  const estadoFormateado = estado.toLowerCase();

  let nombre = `propuestas_${estadoFormateado}`;

  if (sucursal) {
    nombre += `_${sucursal.replace(/\s+/g, "_")}`;
  }

  if (fechaInicio && fechaFin) {
    const inicio = fechaInicio.replace(/\//g, "-");
    const fin = fechaFin.replace(/\//g, "-");
    nombre += `_${inicio}_a_${fin}`;
  }

  nombre += `_${fechaActual}`;

  return nombre;
};
